import React, { useCallback, useEffect, useRef, useContext, useState, useMemo } from "react";
import { Grid, Divider } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import ThreadListContainer from "./threadList";
import { RuleBasedThread, InboxControlsState } from "../../types/ruleBasedInbox";
import { AGENT_ACTIVITY_ACTION_TYPE, DEFAULT_PAGE_LIMIT, DEFAULT_START_FROM } from "./constants";
import { getManualOverrideConfig, parseFiltersFromUrl, createFilterParams, getDefaultInboxControlsState,
    isChannelNameWebsiteOrChronox, getRelatedChannelName, getAzureThreadLink, getOpenAiThreadLink,
    getAgentActionSuccessMsg, scrollToBottomInChat, updateChatbotUserIdParam
} from "./utils";
import globalStyles from "../../assets/styles/global.module.scss";
import styles from "../../assets/styles/componentStyles/chatbotStyles/chatsMobileResponsive.module.scss";
import GlobalPageHeaderText from "../../components/utility/globalPageHeaderText";
import { useRuleBasedInboxStore } from "../../store/ruleBasedInboxStore";
import { UserContext, UserContextPayload } from "../../context/userContext";
import { IntegrationContext, IntegrationContextPayload } from "../../context/integrationContext";
import agent from "../../services/api";
import ChatUserThreadLogView from "./threadView/chatUserThreadLogView";
import ChatInformationBlock from "./threadInfo/chatInformationBlock";
import { currentActiveChatbotThreadTypeNew, manualAgentData } from "../../models/axiosModel";
import { CHRONOX_FEATURES, formatDateAccordingToTimezoneUsingMoment, isFeatureDisabled } from "../../utils/constants";
import { useTicketStore } from "../../store/ticketStore";
import { useGenericStore } from "../../store/genericStore";
import useGenericHook from "../../hooks/useGenericHook";
import clsx from "clsx";
import InboxModals from "./inboxModals";
import { useGetSpecificChat } from "./hooks/useGetSpecificChat";
import { isInstagramChannel } from "./channelConstants";

/**
 * Rule-Based Inbox Page
 * Main component that renders the inbox with thread list, chat view, and context panel
 */
const RuleBasedInbox: React.FC = () => {
    // Context - declare first so it can be used in callbacks
    const { setFlagLoading, userDetail, handleError } = useContext(UserContext) as UserContextPayload;
    const { integrationListNewStructure, getMetaIntegrationList } = useContext(IntegrationContext) as IntegrationContextPayload;

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    }))) 

    // Get state from ruleBasedInbox store (using shallow selector to prevent unnecessary re-renders)
    // Popup/modal flags are subscribed only in InboxModals to avoid re-rendering the page shell.
    const {
        isLoading, selectedThreadId, filters, currentActiveChatbotThread, setCurrentActiveChatbotThread,
        setSourceDetails, setUserTimeZoneHour, chatClicked, setChatClicked, chatInfoClicked,
        flagDebug, setFlagDebug, offset, setOffset, flagHasMoreMessagesToLoad, setFlagHasMoreMessagesToLoad, 
        flagLoadingPreviousMessages, setFlagLoadingPreviousMessages, setListOfIntegration, setManualAgentDataInCurrentThread, 
        flagViewDetailedLog, setFlagViewDetailedLog, getThreadTagApi, checkAgentActivityMsgEnabled, 
        setFlagSendAgentActivityMsgConfirmation, setSelectedThreadId, setSelectedThread, setFilters, 
        fetchThreads, setUserAssignmentId, updateThreadMetadata, clearThreadUnread, clearAllThreadCache, 
        clearThreadFromCache, saveThreadScrollPosition, clearThreadScrollPosition, setSelectedAgentId,
        setNewThreadInfo, newThreadInfo, flagRenderNewConversation, setFlagRenderNewConversation,
        setIsInfoTabLoading, clearAttachmentAnimationTimeout
    } = useRuleBasedInboxStore(
        useShallow((state) => ({
            isLoading: state.isLoading,
            selectedThreadId: state.selectedThreadId,
            filters: state.filters,
            currentActiveChatbotThread: state.currentActiveChatbotThread,
            setCurrentActiveChatbotThread: state.setCurrentActiveChatbotThread,
            setSourceDetails: state.setSourceDetails,
            setUserTimeZoneHour: state.setUserTimeZoneHour,
            chatClicked: state.chatClicked,
            setChatClicked: state.setChatClicked,
            chatInfoClicked: state.chatInfoClicked,
            flagDebug: state.flagDebug,
            setFlagDebug: state.setFlagDebug,
            offset: state.offset,
            setOffset: state.setOffset,
            flagHasMoreMessagesToLoad: state.flagHasMoreMessagesToLoad,
            setFlagHasMoreMessagesToLoad: state.setFlagHasMoreMessagesToLoad,
            flagLoadingPreviousMessages: state.flagLoadingPreviousMessages,
            setFlagLoadingPreviousMessages: state.setFlagLoadingPreviousMessages,
            setListOfIntegration: state.setListOfIntegration,
            setManualAgentDataInCurrentThread: state.setManualAgentDataInCurrentThread,
            flagViewDetailedLog: state.flagViewDetailedLog,
            setFlagViewDetailedLog: state.setFlagViewDetailedLog,
            getThreadTagApi: state.getThreadTagApi,
            checkAgentActivityMsgEnabled: state.checkAgentActivityMsgEnabled,
            setFlagSendAgentActivityMsgConfirmation: state.setFlagSendAgentActivityMsgConfirmation,
            setSelectedThreadId: state.setSelectedThreadId,
            setSelectedThread: state.setSelectedThread,
            setFilters: state.setFilters,
            fetchThreads: state.fetchThreads,
            setUserAssignmentId: state.setUserAssignmentId,
            updateThreadMetadata: state.updateThreadMetadata,
            clearThreadUnread: state.clearThreadUnread,
            clearAllThreadCache: state.clearAllThreadCache,
            clearThreadFromCache: state.clearThreadFromCache,
            saveThreadScrollPosition: state.saveThreadScrollPosition,
            clearThreadScrollPosition: state.clearThreadScrollPosition,
            setSelectedAgentId: state.setSelectedAgentId,
            setNewThreadInfo: state.setNewThreadInfo,
            newThreadInfo: state.newThreadInfo,
            flagRenderNewConversation: state.flagRenderNewConversation,
            setFlagRenderNewConversation: state.setFlagRenderNewConversation,
            setIsInfoTabLoading: state.setIsInfoTabLoading,
            clearAttachmentAnimationTimeout: state.clearAttachmentAnimationTimeout
        }))
    );

    const { getTicketStatusListApi, getTicketFieldApi } = useTicketStore(useShallow((state) => ({
        getTicketStatusListApi: state.getTicketStatusListApi,
        getTicketFieldApi: state.getTicketFieldApi
    })))

    const hasEffectRun = useRef(false);

    const { isScreenBelow1000, isScreenBelow600 } = useGenericHook();
    
    // URL params for tracking selected thread
    const [searchParams, setSearchParams] = useSearchParams();

    const feedback_id = searchParams.get('feedback_id');

    // State for chat loading (used for skeleton loader)
    const [isLoadingChat, setIsLoadingChat] = useState<boolean>(false);

    // State to track if generic calls are in progress (for skeleton visibility)
    const [isLoadingGenericCalls, setIsLoadingGenericCalls] = useState<boolean>(true);

    // Refs to track initialization state
    const genericCallsDone = useRef(false);
    const initialFetchDone = useRef(false);
    const isDoingInitialFetch = useRef(false);
    const chatLoadedFromUrlRef = useRef(false);

    // Holds the cancel function returned by keepScrollPinnedToBottom so we
    // can stop tracking the outgoing thread when the user switches threads.
    const scrollPinCleanupRef = useRef<(() => void) | null>(null);

    // Helper: Sync filters to URL search params
    const syncFiltersToUrl = useCallback((currentFilters: InboxControlsState) => {
        setSearchParams(createFilterParams(currentFilters));
    }, [setSearchParams]);

    // ============ Initialization Flow ============                                                                                                                            
    // Step 1: Fetch generic calls first (thread tags, status, meta integrations)
    // Skeleton should be visible during this phase
    useEffect(() => {
        const fetchGenericCalls = async () => {
            if (!genericCallsDone.current) {
                genericCallsDone.current = true;
                setIsLoadingGenericCalls(true);

                try {
                    // Fetch all generic calls in parallel
                    const genericCalls = [
                        getThreadTagApi(),
                        getMetaIntegrationList(true),
                    ]
                    if(!isFeatureDisabled(userDetail, CHRONOX_FEATURES['THREAD_TICKETING'])){
                        genericCalls.push(getTicketStatusListApi(), getTicketFieldApi());
                    }
                    await Promise.all(genericCalls);
                } catch (error) {
                    console.error("Error fetching generic calls:", error);
                } finally {
                    setIsLoadingGenericCalls(false);
                }
            }
        };

        fetchGenericCalls();
    }, [getThreadTagApi, getTicketStatusListApi, getTicketFieldApi, getMetaIntegrationList]);

    // Step 2: Set user assignment ID for "Assigned to Me" filter
    useEffect(() => {
        if (userDetail?.user_assignment_id) {
            setUserAssignmentId(userDetail.user_assignment_id);
        }
    }, [userDetail?.user_assignment_id, setUserAssignmentId]);

    // Step 3: Sync integration list to inbox store (needed for socket handling)
    useEffect(() => {
        if (integrationListNewStructure) {
            setListOfIntegration(integrationListNewStructure);
        }
    }, [integrationListNewStructure, setListOfIntegration]);

    // Step 4a: Show chat skeleton immediately if chatbot_user_id is in URL (before threads load)
    useEffect(() => {
        const chatbotUserIdFromUrl = searchParams.get("chatbot_user_id");
        if (chatbotUserIdFromUrl && !chatLoadedFromUrlRef.current && chatbotUserIdFromUrl != selectedThreadId) {
            // Show chat skeleton immediately when chatbot_user_id is detected
            setIsLoadingChat(true);
            setIsInfoTabLoading(true);
            setSelectedThreadId(chatbotUserIdFromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]); 

    const getStartFromPageNo = (pageNo: number, limit: number): number => {
        const page = pageNo > 0 ? pageNo : 1;
        return (page - 1) * limit + 1;
    }; 

    // Step 4b: Once integrationListNewStructure is available, fetch threads and specific chat
    useEffect(() => {
        // Wait for generic calls to complete, integrationListNewStructure to be available, and userDetail to be loaded
        // Check if userDetail is loaded (flagUser is not null, meaning user data has been fetched)
        const isUserDetailLoaded = userDetail?.flagUser !== null;

        if (!isLoadingGenericCalls && integrationListNewStructure !== null && !initialFetchDone.current && isUserDetailLoaded) {
            initialFetchDone.current = true;
            isDoingInitialFetch.current = true;

            // Parse filters from URL and merge with defaults
            const urlFilters = parseFiltersFromUrl(searchParams);

            // Get default filters based on user role (Organization Agents get flagAssignedToMe: true)
            const defaultFilters = getDefaultInboxControlsState(userDetail);

            const finalFilters = Object.keys(urlFilters).length > 0
                ? { ...defaultFilters, ...urlFilters }
                : defaultFilters;

            setFilters(finalFilters);

            // Fetch threads (restore page from URL when present)
            const startFrom = getStartFromPageNo(
                Number(searchParams.get("page")) || 1,
                DEFAULT_PAGE_LIMIT
            );

            fetchThreads(startFrom, DEFAULT_PAGE_LIMIT).then(() => {
                isDoingInitialFetch.current = false;

                // After threads are loaded, check if we need to load a specific chat from URL
                const chatbotUserIdFromUrl = searchParams.get("chatbot_user_id");
                if (chatbotUserIdFromUrl && !chatLoadedFromUrlRef.current) {
                    chatLoadedFromUrlRef.current = true;

                    // Call getSpecificChat - it will handle the API call and errors
                    // Note: isLoadingChat is already set to true in Step 4a
                    setFlagDebug(false);
                    setOffset(0);
                    setFlagHasMoreMessagesToLoad(true);
                    setFlagLoadingPreviousMessages(false);
                    setFlagViewDetailedLog(false);
                    getSpecificChat(chatbotUserIdFromUrl, 0, false, false).catch((error) => {
                        // If getSpecificChat fails, remove from params and clear selection
                        console.error("Error loading chat from URL:", error);
                        setSelectedThreadId(null);
                        setIsLoadingChat(false);
                        setIsInfoTabLoading(false);
                        setSearchParams(updateChatbotUserIdParam(null));
                    });
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingGenericCalls, integrationListNewStructure, searchParams, userDetail]);

    // Refetch when filters change and sync to URL (after initial fetch is done)
    useEffect(() => {
        // Skip this effect if we're still doing the initial fetch to prevent double fetch
        if (initialFetchDone.current && !isDoingInitialFetch.current) {
            fetchThreads(DEFAULT_START_FROM, DEFAULT_PAGE_LIMIT);
            syncFiltersToUrl(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]); // Only depend on filters to prevent unnecessary refetches 

    
    // const getFeedbackProvidedTypeApiCall = async () => {
    //     const response = await agent.getFeedbackProvidedType.get();
    //     if (response.status) {
    //         if (response.data) {
    //             setFeedbackProvidedTypeData(response.data);
    //         }
    //     } else {
    //         handleError(response);
    //     }
    // }

    // const getFeedbackLevelApiCall = async () => {
    //     const response = await agent.getFeedbackLevel.get();
    //     if (response.status) {
    //         if (response.data) {
    //             setFeedbackLevelData(response.data);
    //         }
    //     } else {
    //         handleError(response);
    //     }
    // }

    useEffect(() => { 
        // if(!flagShowGetStartedPage(userDetail)){
        //     if (feedbackProvidedTypeData.length === 0) {
        //         getFeedbackProvidedTypeApiCall();
        //     }
        //     if (feedbackLevelData.length === 0) {
        //         getFeedbackLevelApiCall();
        //     }
        // }

        // Cleanup function to clear currentActiveChatbotThread state.
        return () => {
            setCurrentActiveChatbotThread(null);
            setSelectedThreadId(null);
            setSourceDetails({});
            clearAllThreadCache();
            clearAttachmentAnimationTimeout();
        }
    }, [])

    const getSourceDetails = useCallback((
        ipAddress: string,
        chatbot_domain: string,
        source: string,
        phone_number: string | null,
        user_name: string | null,
        last_message_at: string,
        city: string,
        country: string,
        chatbot_user_input_token: number,
        chatbot_user_output_token: number,
        openai_assistant_id: string,
        openai_thread_id: string,
        waba_name: string | null,
        waba_phone_number: string | null,
        flag_azure?: boolean,
        instagram_account_name?: string | null,
        instagram_account_id?: string | null,
        insta_name?: string | null,
        insta_user_name?: string | null
    ) => {
        const timezoneHour = userDetail.timezone_hour;
        const channelSource = getRelatedChannelName(source);
        const formattedLastActivity = last_message_at
            ? formatDateAccordingToTimezoneUsingMoment(last_message_at, timezoneHour, "all")
            : null;
        const playgroundUrl = flag_azure
            ? getAzureThreadLink(openai_assistant_id)
            : getOpenAiThreadLink(openai_assistant_id, openai_thread_id);

        const updatedSourceDetails = isChannelNameWebsiteOrChronox(source) 
            ? {
                Source: channelSource,
                User_name: user_name,
                Phone_number: phone_number,
                Country: country,
                City: city,
                IP_Address: ipAddress,
                Chatbot_Domain: chatbot_domain,
                Last_message_at: formattedLastActivity,
                User_input_token: chatbot_user_input_token,
                User_output_token: chatbot_user_output_token,
                playground_url: playgroundUrl
            }
            : isInstagramChannel(source) ? 
                {
                    Source: channelSource,
                    Account_Name: instagram_account_name,
                    Account_ID: instagram_account_id,
                    Name: insta_name,
                    User_Name: insta_user_name,
                    Last_activity_at: formattedLastActivity,
                    ...(openai_assistant_id ? {
                        playground_url: playgroundUrl
                    } : {})
                }
            :  {
                Source: channelSource,
                Business_Name: waba_name,
                Chatbot_Phone_Number: waba_phone_number,
                User_Name: user_name,
                User_Phone_Number: phone_number,
                User_input_token: chatbot_user_input_token,
                User_output_token: chatbot_user_output_token,
                Last_activity_at: formattedLastActivity,
                ...(openai_assistant_id ? { playground_url: playgroundUrl } : {}),
            }

        setSourceDetails(updatedSourceDetails);
        setUserTimeZoneHour(timezoneHour);
    }, [userDetail.timezone_hour, setSourceDetails, setUserTimeZoneHour]); 

    const { getSpecificChat } = useGetSpecificChat({
        getSourceDetails,
        setIsLoadingChat,
        scrollPinCleanupRef,
    });

    const handleScroll = useCallback(async () => {
        if (!currentActiveChatbotThread) return;
        if (flagLoadingPreviousMessages) return;

        const scrollableElement = document.getElementById("chatUI");
        if (!scrollableElement) return;

        if (scrollableElement.scrollTop === 0) {
            if (flagHasMoreMessagesToLoad) {
                const currentScrollHeight = scrollableElement.scrollHeight;
                const currentScrollTop = scrollableElement.scrollTop;

                await getSpecificChat(
                    currentActiveChatbotThread.chatbot_user_id,
                    offset,
                    flagDebug,
                    flagViewDetailedLog
                );

                requestAnimationFrame(() => {
                    const newScrollHeight = scrollableElement.scrollHeight;
                    const heightDifference = newScrollHeight - currentScrollHeight;
                    scrollableElement.classList.add("scroll-adjusting");
                    scrollableElement.style.scrollBehavior = "auto";
                    scrollableElement.scrollTop = currentScrollTop + heightDifference;
                    setTimeout(() => {
                        scrollableElement.classList.remove("scroll-adjusting");
                        scrollableElement.style.scrollBehavior = "smooth";
                    }, 100);
                });
            }
        }
    }, [
        currentActiveChatbotThread,
        flagLoadingPreviousMessages,
        flagHasMoreMessagesToLoad,
        getSpecificChat,
        offset,
        flagDebug,
        flagViewDetailedLog,
    ]);

    // Attach scroll listener for loading previous messages (same behavior as Chats page)
    useEffect(() => {
        if (!currentActiveChatbotThread) return;

        const setupScrollListener = () => {
            const scrollableElement = document.getElementById("chatUI");
            if (!scrollableElement) return;
            scrollableElement.addEventListener("scroll", handleScroll);
        };

        const timeoutId = setTimeout(setupScrollListener, 100);
        return () => {
            clearTimeout(timeoutId);
            const scrollableElement = document.getElementById("chatUI");
            if (scrollableElement) {
                scrollableElement.removeEventListener("scroll", handleScroll);
            }
        };
    }, [currentActiveChatbotThread, handleScroll]);

    // Wrapper for getManualOverrideConfig to use current user's assignment ID
    const getManualOverrideConfigForCurrentUser = useCallback(
        (thread: currentActiveChatbotThreadTypeNew | null) =>
            getManualOverrideConfig(thread, userDetail?.user_assignment_id),
        [userDetail?.user_assignment_id]
    );

    // Check If Agent Activity Message is Enabled for the current thread
    const flagSendAgentActivityMsg = useMemo(() => {
        return checkAgentActivityMsgEnabled(integrationListNewStructure);
    }, [integrationListNewStructure, currentActiveChatbotThread])

    // Check If Agent Activity Message is Enabled for the current thread and request the manual override
    const requestManualOverride = async (user_assignment_id: string, flag_delete_agent: boolean) => {
        if(currentActiveChatbotThread){
            if(flagSendAgentActivityMsg){
                setSelectedAgentId(user_assignment_id);
                setFlagSendAgentActivityMsgConfirmation(true);
            } else {
                handleToggleManualOverride(user_assignment_id, flag_delete_agent, false);
            }   
        }   
    }

    // Handle the manual override toggle for agent assignment, joining, and leaving conversations
    async function handleToggleManualOverride(user_assignment_id: string, flag_delete_agent: boolean, flag_send_agent_activity_message: boolean) {
        setIsLoadingChat(true)
        // For ruleBasedInbox, we don't use flagRoundLoader
        const currentActiveChatbotThread = useRuleBasedInboxStore.getState().currentActiveChatbotThread;
        if (currentActiveChatbotThread) {
            const alreadyAssigned = currentActiveChatbotThread?.manual_agent_data?.find((agentData: manualAgentData) => String(agentData.user_assignment_id) === String(user_assignment_id));

                if (alreadyAssigned) {
                    if (flag_delete_agent) {
                        // Leave conversation
                        const response = await agent.removeManualAgent.delete(alreadyAssigned.thread_manual_agent_id, flag_send_agent_activity_message);
                        if (response.status) {
                            showSuccess(getAgentActionSuccessMsg('leave', alreadyAssigned?.user_assignment_id, userDetail.user_assignment_id))
                            setManualAgentDataInCurrentThread(AGENT_ACTIVITY_ACTION_TYPE['LEFT'], alreadyAssigned, currentActiveChatbotThread?.chatbot_user_id);
                        } else {
                            handleError(response);
                        }
                    } else {
                        // Join conversation (update status)
                        const response = await agent.updateManualAgentStatus.put(alreadyAssigned.thread_manual_agent_id, flag_send_agent_activity_message);
                        if (response.status) {
                            !isScreenBelow600 && showSuccess(getAgentActionSuccessMsg('join', alreadyAssigned?.user_assignment_id, user_assignment_id))
                            setManualAgentDataInCurrentThread(AGENT_ACTIVITY_ACTION_TYPE['JOIN'], alreadyAssigned, currentActiveChatbotThread?.chatbot_user_id);
                        } else {
                            handleError(response);
                        }
                    }
                } else {
                    // Takeover - add agent to thread
                    const payload = {
                        thread_id: currentActiveChatbotThread.chatbot_user_id,
                        user_assignment_id: user_assignment_id,
                        flag_send_agent_activity_message: flag_send_agent_activity_message
                    };
                    
                    const response = await agent.addThreadManualAgentAPI.post(payload);
                    if (response.status) {
                        !isScreenBelow600 && showSuccess(getAgentActionSuccessMsg('assign', userDetail?.user_assignment_id, user_assignment_id))
                        setManualAgentDataInCurrentThread(AGENT_ACTIVITY_ACTION_TYPE['ASSIGN'], response?.data, currentActiveChatbotThread?.chatbot_user_id);
                    } else {
                        handleError(response);
                    }
                }
                // Socket will update both the thread list and current active chat via static_workflow_inbox notification
        }
        setIsLoadingChat(false)

        // Keep the thread cache in sync after local takeover/join/leave so
        // revisiting this thread does not restore stale manual_agent_data.
        const updatedThread = useRuleBasedInboxStore.getState().currentActiveChatbotThread;
        const threadId = updatedThread?.chatbot_user_id?.toString();
        if (threadId && updatedThread) {
            const cachedEntry = useRuleBasedInboxStore.getState().getThreadFromCache(threadId);
            if (cachedEntry) {
                useRuleBasedInboxStore.getState().setThreadCache(threadId, {
                    ...cachedEntry,
                    data: updatedThread,
                    cachedAt: Date.now(),
                });
            }
        }

        // Use setTimeout to ensure DOM has updated with new messages
        setTimeout(() => {
            scrollToBottomInChat();
        }, 100);
    }

    // Handle archive toggle for rule-based inbox threads
    const handleArchiveToggle = useCallback(
        async (threadId: string, flagArchive: boolean) => {
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));

            try {
                const res = await agent.updateRuleBasedInboxThreadArchiveStatus.put({
                    thread_id: threadId,
                    flag_archive: flagArchive,
                });

                if (res.status) {
                    showSuccess(flagArchive ? "Thread archived successfully." : "Thread unarchived successfully.")

                    if (currentActiveChatbotThread && currentActiveChatbotThread.thread_id === threadId) {
                        setCurrentActiveChatbotThread({
                            ...currentActiveChatbotThread,
                            flag_archive: flagArchive,
                        });
                    }

                    // Update thread in list locally (no API call)
                    // The updateThreadMetadata function will handle removing from list if needed
                    updateThreadMetadata(threadId, { flag_archive: flagArchive });

                    // If archived and filters don't show archived, clear selection
                    if (flagArchive && !(filters.conversationType === "All" && filters.flagArchived)) {
                        // setSelectedThreadId(null);
                        // setSelectedThread(null);
                        // setCurrentActiveChatbotThread(null);
                    }
                } else {
                    handleError(res);
                }
            } catch (error) {
                console.error("Error toggling archive status:", error);
                handleError({ status: false, message: "Failed to update archive status" });
            } finally {
                setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
            }
        },
        [currentActiveChatbotThread, setCurrentActiveChatbotThread, setFlagLoading, handleError, filters.conversationType, filters.flagArchived, updateThreadMetadata]
    );

    /**
     * Force-refresh the currently open thread by bypassing the cache.
     * Called from the refresh icon button in ChatUserThreadLogView.
     */
    const handleRefreshThread = useCallback(() => {
        if (!currentActiveChatbotThread) return;
        const threadId = currentActiveChatbotThread.chatbot_user_id?.toString();
        if (!threadId) return;

        // Evict from cache so a fresh API call is guaranteed
        clearThreadFromCache(threadId);

        // Also clear saved scroll position — force-refresh means "start fresh",
        // so the view should land at the bottom after reload.
        clearThreadScrollPosition(threadId);

        // Reset pagination and debug state (mirrors handleThreadSelect behaviour)
        setFlagDebug(false);
        setOffset(0);
        setFlagHasMoreMessagesToLoad(true);
        setFlagLoadingPreviousMessages(false);
        setFlagViewDetailedLog(false);

        getSpecificChat(threadId, 0, false, false, false, null, true);
    }, [
        currentActiveChatbotThread,
        clearThreadFromCache,
        clearThreadScrollPosition,
        setFlagDebug,
        setOffset,
        setFlagHasMoreMessagesToLoad,
        setFlagLoadingPreviousMessages,
        flagViewDetailedLog,
        setFlagViewDetailedLog,
        getSpecificChat,
    ]);

    // Handle thread selection - calls getSpecificChat API
    const handleThreadSelect = useCallback(
        (thread: RuleBasedThread) => {
            // Stop any image-load scroll-pinning that was active for the
            // outgoing thread before we switch context.
            scrollPinCleanupRef.current?.();
            scrollPinCleanupRef.current = null;

            // Persist scroll position of the thread being navigated away from
            // so we can restore it when the user returns.
            //
            // We store Infinity when the user is at (or near) the bottom so
            // that keepScrollPinnedToBottom re-pins on the way back (handles
            // late-loading images correctly).
            // A real numeric offset means "user had scrolled up" → restore exactly.
            //
            // IMPORTANT: read selectedThreadId directly from the live Zustand
            // store (getState()) rather than from the useCallback closure.
            //
            // ThreadListItem uses a custom React.memo comparison function that
            // does NOT include onThreadClick — so each thread item holds the
            // very first onThreadClick it ever received, which captures the
            // selectedThreadId from the initial render (often null or wrong).
            // Reading getState() bypasses the closure entirely and always gives
            // the correct current thread.
            //
            // Also: only treat the position as "at bottom" when there is
            // meaningful scrollable content (scrollableHeight > 80 px) to avoid
            // false positives when scrollHeight is temporarily small because
            // images have not loaded yet.
            const chatUI = document.getElementById("chatUI");
            const outgoingThreadId = useRuleBasedInboxStore.getState().selectedThreadId;

            if (chatUI && outgoingThreadId) {
                const scrollableHeight = chatUI.scrollHeight - chatUI.clientHeight;
                const atBottom =
                    scrollableHeight > 80 &&
                    chatUI.scrollTop + chatUI.clientHeight >= chatUI.scrollHeight - 80;
                saveThreadScrollPosition(
                    outgoingThreadId,
                    atBottom ? Infinity : chatUI.scrollTop
                );
            }

            if(outgoingThreadId !== thread.thread_id){
                // Clear attachment icon animation
                clearAttachmentAnimationTimeout();
            }

            setSelectedThreadId(thread.thread_id);
            setSelectedThread(thread);
            setSearchParams(updateChatbotUserIdParam(thread.thread_id));

            if(flagRenderNewConversation){
                setFlagRenderNewConversation(false);
            }
            if(newThreadInfo){
                setNewThreadInfo(null);
            }

            // Clear unread indicator when opening the thread
            clearThreadUnread(thread.thread_id);

            // Reset state
            setFlagDebug(false);
            setOffset(0);
            setFlagHasMoreMessagesToLoad(true);
            setFlagLoadingPreviousMessages(false);
            setFlagViewDetailedLog(false);

            getSpecificChat(thread.thread_id, 0, false, false);
        },
        // selectedThreadId intentionally removed from deps — we read the live
        // value via useRuleBasedInboxStore.getState() instead of the closure so
        // that stale ThreadListItem memo callbacks still save for the correct
        // outgoing thread.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setSelectedThreadId, setSelectedThread, setSearchParams, clearThreadUnread, setFlagDebug, setOffset, setFlagHasMoreMessagesToLoad, flagViewDetailedLog, setFlagViewDetailedLog, getSpecificChat, saveThreadScrollPosition, clearAttachmentAnimationTimeout]
    );

    useEffect(() => {
        if (searchParams.get('chatbot_user_id')) {
            setChatClicked(true)
        }
        else if (searchParams.get('chatbot_user_id') === "") {
            setChatClicked(false)
        }
        else {
            setChatClicked(false)
        }

    }, [searchParams, userDetail])

    useEffect(() => {
        if (chatClicked) {
            if (!hasEffectRun.current && currentActiveChatbotThread !== null) {
                const element = document.getElementById("chatUI")
                element?.scrollTo(0, element.scrollHeight)
                hasEffectRun.current = true;
            }
        }
        else {
            hasEffectRun.current = false;
        }
    }, [currentActiveChatbotThread, chatClicked])

    return (
        <>
            <Grid container direction="column" className={clsx(globalStyles.fullHeight, globalStyles.overflowHidden)}>
                {/* Page Header */}
                <Grid item className={globalStyles.fullwidth} sx={{ padding: isScreenBelow600 ? "0px 24px" : "16px 24px" }}>
                    <GlobalPageHeaderText mainHeadText="Inbox" flagMainTextOnly flagTimezone={!isScreenBelow600} />
                </Grid>

                {!isScreenBelow600 && <Divider className={globalStyles.fullwidth} />}

                {/* Main Content */}

                {/* MOBILE */}
                {isScreenBelow1000 ? (
                    <>
                        <Grid item className={clsx(globalStyles.fullwidth, styles.lowerBlock, globalStyles.relativePosition)}>
                            <Grid item className={clsx(globalStyles.fullwidth, !isScreenBelow1000 && globalStyles.absolutePosition)}>
                                <Grid container flexWrap={"nowrap"} justifyContent={"unset"} className={styles.gridContainerMaxWidth}>

                                    {/* THREAD LIST */}
                                    {!feedback_id && (
                                        <ThreadListContainer
                                            isLoading={isLoadingGenericCalls || isLoading}
                                            onThreadSelect={handleThreadSelect}
                                            flagMobile={true}
                                        />
                                    )}

                                    <ChatUserThreadLogView
                                        getSourceDetails={getSourceDetails}
                                        getSpecificChat={getSpecificChat}
                                        handleToggleManualOverride={requestManualOverride}
                                        getManualOverrideConfig={getManualOverrideConfigForCurrentUser}
                                        flagRuleBasedInbox
                                        onArchiveToggle={handleArchiveToggle}
                                        isLoadingChat={isLoadingChat}
                                        onRefreshThread={handleRefreshThread}
                                        handleTakeoverConversation={handleToggleManualOverride}
                                    />
                                    <Grid />

                                    <Divider orientation='vertical' />

                                    {/* CHAT INFORMATION BLOCK */}
                                    {chatInfoClicked &&
                                        <ChatInformationBlock
                                            handleToggleManualOverride={requestManualOverride}
                                            flagMobile
                                            flagSendAgentActivityMsg={flagSendAgentActivityMsg}
                                        />
                                    }

                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                    ) : (
                        // DESKTOP
                        <Grid item className={clsx(globalStyles.fullwidth, globalStyles.relativePosition, globalStyles.overflowHidden)} sx={{ flex: 1, display: "flex" }}>
                            <Grid container className={clsx(globalStyles.relativePosition, globalStyles.fullHeight)} sx={{ flexWrap: "nowrap"}}>
                                <Grid item>
                                    <ThreadListContainer
                                        isLoading={isLoadingGenericCalls || isLoading}
                                        onThreadSelect={handleThreadSelect}
                                        flagMobile={false}
                                    />
                                </Grid>

                                <Divider orientation="vertical" flexItem />

                                <ChatUserThreadLogView
                                    getSourceDetails={getSourceDetails}
                                    getSpecificChat={getSpecificChat}
                                    handleToggleManualOverride={requestManualOverride}
                                    getManualOverrideConfig={getManualOverrideConfigForCurrentUser}
                                    flagRuleBasedInbox
                                    onArchiveToggle={handleArchiveToggle}
                                    isLoadingChat={isLoadingChat}
                                    onRefreshThread={handleRefreshThread}
                                    handleTakeoverConversation={handleToggleManualOverride}
                                />

                                <ChatInformationBlock
                                    handleToggleManualOverride={requestManualOverride}
                                    flagSendAgentActivityMsg={flagSendAgentActivityMsg}
                                />
                            </Grid>
                        </Grid>
                )}
            </Grid>

            <InboxModals
                flagSendAgentActivityMsg={flagSendAgentActivityMsg}
                handleToggleManualOverride={handleToggleManualOverride}
                getManualOverrideConfig={getManualOverrideConfigForCurrentUser}
                getSpecificChat={getSpecificChat}
            />
        </>
    );
};

export default RuleBasedInbox;
